import { Button, Card, Checkbox, Form, Input, message } from "antd";
import { useEffect, useState } from "react";
import { FiArrowRight, FiEye, FiEyeOff, FiLock, FiMail } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import apiClient from "../../Actions/apiClient";
import "./Login.scss";

export default function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("PHARMA_SOS_USER")) {
      navigate("/app/contactus");
    }
  }, [navigate]);

  const onLogin = async (values) => {
    try {
      setLoading(true);
      const { data } = await apiClient.post("auth", values);
      
      if (data?.result) {
        dispatch({ type: "GET_USER", payload: data.result });
        localStorage.setItem("PHARMA_SOS_USER", data.token);
        
        if (rememberMe) {
          localStorage.setItem("PHARMA_SOS_EMAIL", values.email);
        }
        
        message.success("Login successful!");
        navigate("/app/contactus");
      }
    } catch (error) {
      console.error("Login error:", error);
      message.error(error.response?.data?.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const savedEmail = localStorage.getItem("PHARMA_SOS_EMAIL");

  return (
    <div className="login-page">
      {/* Background Gradient */}
      <div className="login-background">
        <div className="gradient-orb gradient-orb-1"></div>
        <div className="gradient-orb gradient-orb-2"></div>
      </div>

      {/* Main Content */}
      <div className="login-container">
        <div className="login-wrapper">
          {/* Left Section - Branding */}
          <div className="login-branding">
            <div className="brand-content">
              <div className="brand-icon">
                <span>🏥</span>
              </div>
              <h1>Pharma SOS</h1>
              <p>Admin Portal</p>
              <p className="brand-description">
                Welcome back! Access your admin dashboard to manage events, speakers, and more.
              </p>
              
              <div className="features">
                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>Secure Authentication</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>Full Event Management</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>Real-time Updates</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Section - Login Form */}
          <div className="login-form-section">
            <Card className="login-card" bordered={false}>
              <div className="form-header">
                <h2>Sign In</h2>
                <p>Enter your credentials to access the admin panel</p>
              </div>

              <Form
                form={form}
                layout="vertical"
                onFinish={onLogin}
                initialValues={{ 
                  email: savedEmail || "",
                  password: "",
                  rememberMe: !!savedEmail
                }}
                className="login-form"
              >
                {/* Email Field */}
                <Form.Item
                  name="email"
                  label="Email Address"
                  rules={[
                    { required: true, message: "Please enter your email" },
                    { type: "email", message: "Please enter a valid email" }
                  ]}
                >
                  <Input
                    size="large"
                    placeholder="you@example.com"
                    prefix={<FiMail className="form-icon" />}
                    className="login-input"
                  />
                </Form.Item>

                {/* Password Field */}
                <Form.Item
                  name="password"
                  label="Password"
                  rules={[
                    { required: true, message: "Please enter your password" },
                    { min: 6, message: "Password must be at least 6 characters" }
                  ]}
                >
                  <Input
                    size="large"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    prefix={<FiLock className="form-icon" />}
                    suffix={
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <FiEyeOff /> : <FiEye />}
                      </button>
                    }
                    className="login-input"
                  />
                </Form.Item>

                {/* Remember Me & Forgot Password */}
                <div className="form-actions">
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  >
                    Remember me
                  </Checkbox>
                  <a href="#forgot" className="forgot-password">
                    Forgot password?
                  </a>
                </div>

                {/* Submit Button */}
                <Form.Item>
                  <Button
                    type="primary"
                    size="large"
                    block
                    loading={loading}
                    htmlType="submit"
                    className="login-button"
                  >
                    Sign In
                    <FiArrowRight size={16} className="ms-2" />
                  </Button>
                </Form.Item>

                {/* Footer Text */}
                <div className="form-footer">
                  <p>
                    Need help? <a href="mailto:support@pharmasos.com">Contact Support</a>
                  </p>
                </div>
              </Form>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
